import { FormLabel, FormErrorMessage, Flex, FormControl, Select, FormHelperText, Input, Box } from "@chakra-ui/react";
import { Button, MultiSelectControlled, useConfirm } from "am-tax-fe-core";
import { SubmitHandler, useForm } from "react-hook-form";
import { useCreateReport, useDeleteReport, useUpdateReport } from "../../../api/reports/reportQueries";
import { CreateReport, ReportByClientId as Report, UpdateReport } from "../../../api/reports/reportApi";
import { Jurisdiction } from "../../../api/jurisdictions/jurisdictionApi";
import { Entity } from "../../../api/entities/entityApi";
import { useCreateReportEntity, useGetReportEntity, useUpdateReportEntity } from "../../../api/reportEntities/reportEntityQueries";
import { useCreateReportTransaction, useUpdateReportTransaction } from "../../../api/reportTransactions/reportTransactionQueries";
import { TransactionSelector } from "./TransactionSelector";

interface Props {
    onClose: () => void;
    reportData: Report | undefined;
    existingReports: Report[] | undefined;
    isEdit: boolean;
    clientId: string;
    jurisdictions: Jurisdiction[];
    entities: Entity[];
}

type FormFields = {
    year: string;
    type: string;
    jurisdictionId: string;
    primaryEntity: string;
    clientId: string;
    underlyingEntities: string[];
    transactionIds: string[];
    fiscalYearEndOverride: string | undefined;
};

export const CreateOrEditReportForm = ({ reportData, isEdit, onClose, clientId, jurisdictions, entities }: Props) => {
    const createReportMutation = useCreateReport();
    const updateReportMutation = useUpdateReport();
    const deleteReportMutation = useDeleteReport();
    const { data: reportEntity = [] } = useGetReportEntity({ reportId: reportData?.id, leadEntityId: reportData?.leadEntityId });
    const { mutateAsync: updateReportEntityMutation } = useUpdateReportEntity();
    const { mutateAsync: createReportEntityMutation } = useCreateReportEntity();
    const { mutateAsync: createReportTransactionMutation } = useCreateReportTransaction();
    const { mutateAsync: updateReportTransactionMutation } = useUpdateReportTransaction();

    // Generate year options (current year ± 5 years)
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

    const formatDate = (dateString?: string | null) => {
        if (!dateString || dateString === null) return undefined;
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "";
        return date.toISOString().slice(0, 10); // yyyy-MM-dd
    };

    const formValues = {
        year: reportData?.year || currentYear.toString(),
        type: reportData?.type || "",
        jurisdictionId: reportData?.jurisdictionId || "",
        clientId: clientId,
        primaryEntity: reportData?.leadEntityId || "",
        underlyingEntities: reportEntity.supportEntities || [],
        transactionIds: [],
        fiscalYearEndOverride: formatDate(reportData?.fiscalYearEndOverride),
    };

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        setValue,
        watch,
    } = useForm<FormFields>({ values: formValues });

    const prompt = "Are you sure you want to delete the report?";
    const { ConfirmDialog } = useConfirm({ title: "Delete Report", prompt: prompt });

    const handleDelete = async () => {
        if (!reportData?.id) return;
        await deleteReportMutation.mutateAsync(reportData.id);
        onClose();
    };

    const onSubmit: SubmitHandler<FormFields> = async data => {
        if (isEdit && reportData?.id) {
            const reportPayload: UpdateReport = {
                id: reportData.id,
                jurisdictionId: data.jurisdictionId,
                clientId: clientId,
                status: "New", // Default status since it's no longer user-selectable
                type: data.type,
                year: data.year,
                fiscalYearEndOverride: data.fiscalYearEndOverride || undefined, // Optional field
            };
            await updateReportMutation.mutateAsync(reportPayload);
            await updateReportEntityMutation({
                reportId: reportData.id,
                leadEntityId: data.primaryEntity,
                supportEntities: data.underlyingEntities,
            });

            await updateReportTransactionMutation({
                reportId: reportData.id,
                clientId: clientId,
                data: {
                    reportId: reportData.id,
                    transactionIds: data.transactionIds,
                },
            });
        } else {
            const reportPayload: CreateReport = {
                jurisdictionId: data.jurisdictionId,
                clientId: clientId,
                status: "New", // Default status since it's no longer user-selectable
                type: data.type,
                year: data.year,
                fiscalYearEndOverride: data.fiscalYearEndOverride || undefined, // Optional field
            };
            const reportId = (await createReportMutation.mutateAsync(reportPayload)).id;
            await createReportEntityMutation({
                reportId: reportId,
                leadEntityId: data.primaryEntity,
                supportEntities: data.underlyingEntities,
            });

            // Save transaction selections to the report
            if (data.transactionIds && data.transactionIds.length > 0) {
                await createReportTransactionMutation({
                    reportId: reportId,
                    transactionIds: data.transactionIds,
                });
            }
        }
        onClose();
    };

    const handleEntitySelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const entity = entities.find(entity => entity.id === event.target.value);
        if (!entity) return;
        setValue("fiscalYearEndOverride", entity.yearEnded ? new Date(entity.yearEnded).toISOString().slice(0, 10) : "");
    };

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Flex gap="2rem" flexFlow="column nowrap">
                    {/* Fiscal/Tax dropdown and Year dropdown */}
                    <Flex gap="1rem">
                        <FormControl isInvalid={!!errors.type} flex="1">
                            <FormLabel>Report Type:</FormLabel>
                            <Select {...register("type", { required: "Report type is required" })} placeholder="Select Type">
                                <option value="US">US Documentation</option>
                                <option value="OECDLocal">OECD Local File</option>
                                <option value="OECDMaster">OECD Master File</option>
                            </Select>
                            <FormErrorMessage>{errors.type?.message}</FormErrorMessage>
                        </FormControl>
                        <FormControl isInvalid={!!errors.year} flex="1">
                            <FormLabel>Year</FormLabel>
                            <Select {...register("year", { required: "Year is required" })} placeholder="Select Year">
                                {yearOptions.map(year => (
                                    <option key={year} value={year.toString()}>
                                        {year}
                                    </option>
                                ))}
                            </Select>
                            <FormErrorMessage>{errors.year?.message}</FormErrorMessage>
                        </FormControl>
                    </Flex>

                    {/* Jurisdiction of Report */}
                    <FormControl isInvalid={!!errors.jurisdictionId}>
                        <FormLabel>Jurisdiction of Report</FormLabel>
                        <Select placeholder="Select jurisdiction" {...register("jurisdictionId", { required: "Jurisdiction is required" })}>
                            {/* Demo jurisdictions - using latest created IDs */}
                            {jurisdictions.map(jurisdiction => (
                                <option key={jurisdiction.id} value={jurisdiction.id}>
                                    {jurisdiction.name}
                                </option>
                            ))}
                        </Select>
                        <FormErrorMessage>{errors.jurisdictionId?.message}</FormErrorMessage>
                    </FormControl>

                    {/* Lead Entity */}
                    <Flex gap="1rem">
                        <FormControl isInvalid={!!errors.primaryEntity}>
                            <FormLabel>Lead Entity</FormLabel>
                            {!entities || entities.length === 0 ? (
                                <>
                                    <Box>No entities exist for the client. Please create an entity.</Box>
                                </>
                            ) : (
                                <>
                                    <Select
                                        placeholder="Select entity"
                                        {...register("primaryEntity", { required: "Lead Entity is required" })}
                                        onChange={handleEntitySelect}
                                    >
                                        {(Array.isArray(entities) ? entities : []).map(entity => (
                                            <option key={entity.id} value={entity.id}>
                                                {entity.abbreviatedName}
                                            </option>
                                        ))}
                                    </Select>

                                    <FormErrorMessage>{errors.primaryEntity?.message}</FormErrorMessage>
                                </>
                            )}
                        </FormControl>
                        <FormControl isInvalid={!!errors.fiscalYearEndOverride}>
                            <FormLabel>Year End</FormLabel>
                            <Input
                                type="date"
                                {...register("fiscalYearEndOverride", { required: false })}
                                placeholder="Select year end"
                                value={watch("fiscalYearEndOverride") || ""}
                                onChange={e => setValue("fiscalYearEndOverride", e.target.value)}
                            />
                        </FormControl>
                    </Flex>

                    {/* Underlying Entities */}
                    <FormControl isInvalid={!!errors.underlyingEntities}>
                        <FormLabel>Underlying Entities</FormLabel>
                        {!entities || entities.length === 0 ? (
                            <>
                                <Box>No entities exist for the client. Please create an entity.</Box>
                            </>
                        ) : (
                            <>
                                <MultiSelectControlled
                                    control={control}
                                    options={(Array.isArray(entities) ? entities : []).map(entity => ({
                                        value: entity.id,
                                        label: `${entity.abbreviatedName}`,
                                    }))}
                                    name={"underlyingEntities"}
                                />
                                <FormHelperText>Select one or more underlying entities</FormHelperText>
                                <FormErrorMessage>{errors.underlyingEntities?.message as string}</FormErrorMessage>
                            </>
                        )}
                    </FormControl>

                    {/* Transaction Selection */}
                    <TransactionSelector control={control} name="transactionIds" clientId={clientId} reportId={reportData?.id} isRequired={false} />

                    <Flex marginBottom=".75rem" justifyContent="space-between">
                        {isEdit && (
                            <Button variant="ghost" colorScheme="red" onClick={handleDelete}>
                                Delete Report
                            </Button>
                        )}
                        <Button type="submit" variant="primary" isLoading={createReportMutation.isPending || updateReportMutation.isPending}>
                            {isEdit ? "Update" : "Add"}
                        </Button>
                    </Flex>
                </Flex>
            </form>
            <ConfirmDialog />
        </>
    );
};
